import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import { useFilters, useSortBy, usePagination } from 'react-table';
import { getCajas, deleteCaja, putCaja } from "../helpers/rutaCajas";
import ModalFormCaja from "../components/ModalFormCaja";
import Table from "./TableContainer";
import "../css/tablecomandas.css";

const Styles = styled.div`
  .table-container { overflow: auto; height: 600px; }
  table { border: 1px solid black; border-spacing: 0; font-size: 13px; }
  th, td { padding: 0.5rem; border-bottom: 1px solid black; border-right: 1px solid black; }
  th { background-color: #778899; position: sticky; top: 0; z-index: 2; text-align: center; font-size: 12px; }
  td { background-color: white; }
  .pagination { padding: 0.5rem; background-color: #778899; font-size: 15px; font-weight: bold; }
`;

export default function AppCajaReactTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cajaEdit, setCajaEdit] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchData = useCallback(async () => {
    const resp = await getCajas();
    if (!resp?.ok) {
      console.error(resp?.error || "Error al obtener cajas");
      setData([]);
    } else {
      setData(resp.cajas || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // borrar → usa _id y refresca lista
  const borrar = useCallback(async (_id) => {
    if (!window.confirm("¿Eliminar caja?")) return;
    try {
      await deleteCaja(_id);
    } finally {
      fetchData();
    }
  }, [fetchData]);

  // editar → guarda con _id y refresca lista
  const onSave = useCallback(async (id, values) => {
    try {
      await putCaja(id, values);
    } finally {
      setShowModal(false);
      setCajaEdit(null);
      fetchData();
    }
  }, [fetchData]);

  const columns = useMemo(() => [
    // Editar
    {
      id: 'editar',
      Header: 'Edita',
      accessor: '_id',
      width: 60,
      Cell: ({ row }) => (
        <button
          className="action-btn action-btn--edit"
          title="Editar"
          onClick={() => { setCajaEdit(row.original); setShowModal(true); }}
        >
          ✏️
        </button>
      ),
    },
    // Eliminar
    {
      id: 'eliminar',
      Header: '🗑️',
      accessor: '_id',
      width: 60,
      Cell: ({ row }) => (
        <button
          className="action-btn action-btn--del"
          title="Eliminar"
          onClick={() => borrar(row.original._id)}
        >
          🗑️
        </button>
      ),
    },

    // Muestra el código, pero NO lo usamos como id
    { Header: 'Nro', accessor: 'codcaj', width: 70 },

    {
      Header: 'Fecha',
      accessor: r => (r.fecha ? new Date(r.fecha) : null),
      id: 'fecha',
      width: 150,
      Cell: ({ value }) =>
        value instanceof Date
          ? value.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" })
          : '',
    },

    { Header: 'Descripción', accessor: 'descripcion', width: 400 },

    // 👇 NUEVA COLUMNA (después de Descripción)
    {
      Header: 'Tipo',
      accessor: (r) => (r.tipo || '').toUpperCase(), // CAJA | BANCO
      id: 'tipo',
      width: 110,
      Cell: ({ value }) => value || '',
    },

    {
      Header: 'Monto Inicial',
      accessor: 'montoInicial',
      width: 150,
      Cell: ({ value }) => (
        <div style={{ textAlign: 'right' }}>
          {value != null ? value.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' }) : ''}
        </div>
      ),
      Footer: info => {
        const total = info.rows.reduce((sum, row) => sum + (row.values.montoInicial || 0), 0);
        return (
          <div style={{ textAlign: 'right', fontWeight: 'bold', color: 'red' }}>
            {total.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
          </div>
        );
      }
    },

    {
      Header: 'Saldo Actual',
      accessor: 'saldoActual',
      width: 150,
      Cell: ({ value }) => (
        <div style={{ textAlign: 'right' }}>
          {value != null ? value.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' }) : ''}
        </div>
      ),
      Footer: info => {
        const total = info.rows.reduce((sum, row) => sum + (row.values.saldoActual || 0), 0);
        return (
          <div style={{ textAlign: 'right', fontWeight: 'bold', color: 'red' }}>
            {total.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
          </div>
        );
      }
    },

    {
      Header: 'Diferencia',
      accessor: r => (r.montoInicial || 0) - (r.saldoActual || 0),
      id: 'diferencia',
      width: 150,
      Cell: ({ value }) => (
        <div style={{ textAlign: 'right', color: value < 0 ? 'red' : 'inherit' }}>
          {value != null
            ? value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : ''}
        </div>
      ),
      Footer: info => {
        const total = info.rows.reduce(
          (sum, row) => sum + ((row.values.montoInicial || 0) - (row.values.saldoActual || 0)),
          0
        );
        return (
          <div style={{ textAlign: 'right', fontWeight: 'bold', color: total < 0 ? 'red' : 'blue' }}>
            {total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        );
      }
    }
  ], [borrar]);

  return (
    <div className="container mt-4">
      <h3 className="text-center mb-4">LISTADO DE CAJAS</h3>
      <Styles>
        <div className="table-container">
          <Table
            columns={columns}
            data={data}
            loading={loading}
            plugins={[useFilters, useSortBy, usePagination]}
          />
        </div>
      </Styles>

      {cajaEdit && (
        <ModalFormCaja
          show={showModal}
          handleClose={() => { setShowModal(false); setCajaEdit(null); fetchData(); }}
          caja={cajaEdit}
          onSave={onSave}
        />
      )}
    </div>
  );
}
